import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import useCommunityPopup from '@/hooks/community/board/useCommunityPopup';
import useGetBoardCategories from '@/hooks/community/useGetBoardCategories';
import useGetNewestBoardPost from '@/hooks/community/useGetNewestBoardPost';
import useCustomRouter from '@/hooks/useCustomRouter';
import { tokenState } from '@/recoil/atoms/common/auth';
import { Client } from '@/types/app';
import { BoardCategory } from '@/types/bff/gql-schema';
import { BOARD_TYPE, ROUTES } from '@/types/common';
import { BoardPostVo } from '@/types/community';
import { mobileLogger } from '@/utils/logger';

import useAuthModal from '../useAuthModal';
import usePostReportModal from '../usePostReportModal';
import { REPORT_TYPE } from '../useReportModal';
import useToast from '../useToast';

const useCommunityListViewModel = (client: Client) => {
  const { onOpenCommunityCloseNotice } = useCommunityPopup(client);
  const token = useRecoilValue(tokenState);
  const {
    moveToPage,
    pageName,
    router: { query, replace },
  } = useCustomRouter();

  const [fixedBottomSheetWidth, setFixedBottomSheetWidth] = useState<number>(0); // WriteFixedBottomSheet
  const [selectedCategory, setSelectedCategory] = useState<BoardCategory>();
  const [isVisibleBottomSheet, setIsVisibleBottomSheet] = useState<boolean>(false);

  const { showToast } = useToast();
  const { setShowAuthModal } = useAuthModal();
  const { showReportModal, onShowReportModal, onCloseReportModal, reportData, reportItems } = usePostReportModal();

  const { categories } = useGetBoardCategories();
  const {
    newestPostsDataRefetch,
    isNewestPostsLoading,
    hasNewestPostsNextPage,
    fetchNewestPostsNextPage,
    newestPostsData,
  } = useGetNewestBoardPost(selectedCategory?.boardCategoryId);

  const onToggleBottomSheet = (on = true) => {
    setIsVisibleBottomSheet(on);
  };

  const onClickCategory = (category: BoardCategory) => {
    mobileLogger({
      logger: {
        eventName: 'comm_header_category_filter_click',
        pageName,
        payload: {
          categoryId: category.boardCategoryId,
        },
      },
    });
    setSelectedCategory(category);
    replace({ query: { ...query, categoryId: category.boardCategoryId } }, undefined, {
      shallow: true,
    });
    onToggleBottomSheet(false);
  };

  const onClickAllCategory = () => {
    mobileLogger({
      logger: {
        eventName: 'comm_header_category_filter_click',
        pageName,
        payload: {
          categoryId: 'all',
        },
      },
    });
    setSelectedCategory(undefined);
    replace({}, undefined, {
      shallow: true,
    });
    onToggleBottomSheet(false);
  };

  const onClickSearchIcon = () => {
    moveToPage({
      webLink: `${ROUTES.COMMON.SEARCH.path}?type=${BOARD_TYPE.COMMUNITY}&categoryId=${selectedCategory?.boardCategoryId}&categoryName=${selectedCategory?.name}`,
      mobileScreenName: ROUTES.COMMON.SEARCH.appScreenName,
      params: {
        type: BOARD_TYPE.COMMUNITY,
        categoryId: selectedCategory?.boardCategoryId ?? ('' as string),
        categoryName: selectedCategory?.name ?? ('' as string),
      },
    });
  };

  const onClickPost = (e: React.MouseEvent, post: BoardPostVo) => {
    e.preventDefault();
    if (post.blinded) {
      return showToast({ message: '신고로 인해 삭제 처리된 게시글입니다.' });
    }
    mobileLogger({
      logger: {
        eventName: 'post_item_click',
        pageName,
        payload: {
          id: post.boardPostPid,
        },
      },
    });
    moveToPage({
      webLink: `${ROUTES.COMMUNITY.DETAIL.path}/${post.boardPostPid}`,
      mobileScreenName: ROUTES.COMMUNITY.DETAIL.appScreenName,
      params: {
        id: post.boardPostPid,
      },
    });
  };

  const onRefresh = async () => {
    await newestPostsDataRefetch();
  };

  // 질문 신고하기
  const onClickReport = (e: React.MouseEvent, post: BoardPostVo) => {
    e.preventDefault();
    e.stopPropagation();

    if (!token) {
      setShowAuthModal(true);
      return;
    }

    onShowReportModal({
      targetId: post.boardPostPid,
      targetType: REPORT_TYPE.BOARD_POST,
      isMine: post.isMine,
    });
  };

  useEffect(() => {
    if (query.categoryId && categories) {
      const queryCategory = categories.filter((category) => category.boardCategoryId === query.categoryId)[0];
      setSelectedCategory(queryCategory);
    }
  }, [query, categories]);

  useEffect(() => {
    const resize = () => {
      setFixedBottomSheetWidth(document.getElementById('postList')?.clientWidth || 0);
    };

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    setFixedBottomSheetWidth(document.getElementById('postList')?.clientWidth || 0);
    onOpenCommunityCloseNotice();
  }, []);

  return {
    categories,
    newestPostsDataRefetch,
    isNewestPostsLoading,
    hasNewestPostsNextPage,
    fetchNewestPostsNextPage,
    newestPostsData,
    isVisibleBottomSheet,
    onClickCategory,
    onToggleBottomSheet,
    selectedCategory,
    onClickSearchIcon,
    fixedBottomSheetWidth,
    onClickAllCategory,
    pageName,
    showReportModal,
    onCloseReportModal,
    reportItems,
    reportData,
    onRefresh,
    onClickPost,
    onClickReport,
  };
};

export default useCommunityListViewModel;
